import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
/**
 * Lista de importações (batches). Com `accountId`, filtra pela conta; com
 * `includeAll` (modo geral, sem conta selecionada), lista todos os lotes da org.
 */
export const useStatementBatches = (accountId, options) => {
    const includeAll = options?.includeAll ?? false;
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['statement-batches', activeOrganizationId, accountId ?? '__all__'],
        enabled: orgQueryReady && (Boolean(accountId) || includeAll),
        queryFn: async () => {
            const { data } = await apiClient.get('/imports/statement/batches', { params: accountId ? { accountId } : {} });
            return data;
        },
    });
};
/** Chave de query usada acima — útil para invalidar após import/delete. */
export const statementBatchesQueryKey = (organizationId, accountId) => ['statement-batches', organizationId, accountId ?? '__all__'];
/** Detalhe de um batch (transações). Só busca quando habilitado. */
export const useStatementBatchDetail = (batchId) => useQuery({
    queryKey: ['statement-batch-detail', batchId],
    enabled: Boolean(batchId),
    queryFn: async () => {
        const { data } = await apiClient.get(`/imports/statement/batches/${batchId}`);
        return data;
    },
});
export const useDeleteStatementBatch = () => useMutation({
    mutationFn: async (batchId) => {
        const { data } = await apiClient.delete(`/imports/statement/batches/${batchId}`);
        return data;
    },
});
/** Mapeamentos de categoria por descrição (auto-categorização de imports). */
export const useCategoryMappings = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    const mappingsQuery = useQuery({
        queryKey: ['category-mappings', activeOrganizationId],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/imports/statement/category-mappings');
            return data;
        },
    });
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['category-mappings', activeOrganizationId] });
    const createMapping = useMutation({
        mutationFn: async (dto) => {
            const { data } = await apiClient.post('/imports/statement/category-mappings', dto);
            return data;
        },
        onSuccess: () => void invalidate(),
    });
    const deleteMapping = useMutation({
        mutationFn: async (id) => {
            await apiClient.delete(`/imports/statement/category-mappings/${id}`);
        },
        onSuccess: () => void invalidate(),
    });
    return {
        ...mappingsQuery,
        mappings: mappingsQuery.data ?? [],
        createMapping,
        deleteMapping,
    };
};
